import {
  Component,
  Input,
  OnInit,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AntdModule } from '@core/config/antd.module';
import { AppSectionComponent } from '@components/layout/section/app-section.component';
import { AppElementComponent } from '@components/layout/element/app-element.component';

import { FormModalsHost } from './form-modals.types';

/**
 * Modales estándar de las pantallas de formulario: el de sección y el simple.
 *
 * Antes este marcado estaba copiado en 18 plantillas de `src/app/templates` (960 líneas).
 * Las copias habían divergido en detalles accidentales, así que aquí se consolida una única
 * versión y se toma la variante correcta de cada divergencia.
 *
 * Se usa como hermano de `<app-form-layout>`, recibiendo el propio formulario como host:
 *
 * ```html
 * <app-form-modals [host]="this"></app-form-modals>
 * ```
 *
 * Todo el marcado específico de ng-zorro queda encerrado aquí: una plantilla de feature ya
 * no menciona `nz-modal`, lo que permite sustituir este componente por una versión Ionic
 * intercambiando el barrel `@shared/shared-standalone` en el build.
 */
@Component({
  standalone: true,
  selector: 'app-form-modals',
  imports: [AntdModule, AppSectionComponent, AppElementComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app-form-modals.component.html',
})
export class AppFormModalsComponent implements OnInit {
  /** Formulario dueño del estado. En la práctica, siempre un `AppBaseFormComponent`. */
  @Input({ required: true }) host!: FormModalsHost;

  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);

  /*
    Este componente es OnPush y su único input es `host`, cuya referencia nunca cambia: el
    formulario se pasa a sí mismo con `[host]="this"`. El estado de los modales, en cambio, se
    muta en sitio (`this.sectionModal.visible = true`), así que nada marcaba esta vista y el
    diálogo no abría ni cerraba. El host avisa por `modalesCambiaron` y aquí se marca.

    Mismo patrón que `marcarAlPropagar` para los renderers de sección; ver
    `GUIA-ONPUSH-FORMULARIOS.md`.
  */
  ngOnInit(): void {
    this.host?.modalesCambiaron?.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => this._cdr.markForCheck());
  }

  /**
   * Estilos del cuerpo del modal de sección.
   *
   * `height` y `maxHeight` se descartaban en `showSectionModalDialog`, así que las
   * plantillas que los leían recibían siempre `undefined`. Ya se propagan; las claves con
   * valor `undefined` no se aplican, de modo que los formularios que no las configuran
   * conservan exactamente el estilo anterior.
   */
  get sectionBodyStyle(): Record<string, any> {
    const properties = this.host?.sectionModal?.properties ?? {};
    return {
      'min-height': properties['minHeight'],
      height: properties['height'],
      'max-height': properties['maxHeight'],
    };
  }

  /** La sección aporta sus propias acciones para el footer. */
  get hasSectionActions(): boolean {
    return !!this.host?.sectionModal?.actions?.subSectionElements?.length;
  }

  /**
   * Hay footer cuando la sección trae acciones, o cuando corresponde el botón "Cerrar" de
   * respaldo. Con `hideFooterClose` y sin acciones no se pinta barra alguna, para no dejar
   * un footer vacío.
   */
  get showSectionFooter(): boolean {
    return this.hasSectionActions || !this.host?.sectionModal?.properties?.['hideFooterClose'];
  }
}
